import { Button, InlineMessage, PageContent, PageHeader, ReportIcon, Spinner, Stack, ViewShareDialog } from '@olytic/foundation';
import {
  type ReportFolder,
  ReportFolderDialog,
  ReportList,
  type ReportType,
  ReportTypePicker,
  type SavedReportSummary,
} from '@olytic/foundation/reports';
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import { MY_DASHBOARD_ROUTE, reportDashboardHref, REPORTS_ROUTE } from '../navigation';
import { REPORT_TIME_ZONE, useReportTypes } from './reportSchema';
import {
  createFolder, deleteFolder, deleteReport, duplicateReport, hasStandardFolder, installStandardReports, moveReport, renameReport,
  setFavorite, setFavoriteType, standardReportsNotice, updateFolder, useLibrary,
} from './reportStore';

/**
 * /reports: the saved reports and their folders, as Foundation's ReportList. Every folder's
 * menu opens it as a dashboard (/reports/dashboard/:folderId, ReportDashboardPage.tsx;
 * MOTHER-162), and the header's Open My Dashboard opens the viewer's favorites as one
 * (/reports/dashboard/favorites; MOTHER-293). A report's Duplicate copies it into the same folder (duplicateReport;
 * MOTHER-193); its Rename… renames it where it is (renameReport; MOTHER-202). An Admin gets a
 * standard reports button in the header (MOTHER-212): Add standard reports while the library has
 * no public top-level `Standard reports` folder, Update standard reports once it has (MOTHER-248).
 * Either installs them (POST /api/reports/standard; MOTHER-211), which adds only the reports the
 * folder doesn't hold yet, then opens the folder and says how many were added. The server decides
 * what is missing, so the page never does. The server refuses anyone else, so nobody else sees
 * it. The fetcher is the app's authenticated fetch (App.tsx).
 */

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

const TIME_ZONE = REPORT_TIME_ZONE;

const typeIn = (types: ReportType[] | null, key: string | null | undefined): ReportType | undefined =>
  types?.find((t) => t.key === key);

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');

type FolderDialog = { mode: 'new'; parentId: string | null } | { mode: 'rename'; folder: ReportFolder } | null;

export function ReportsListPage({ fetcher }: { fetcher: Fetch }): JSX.Element {
  useDocumentTitle('Reports');
  const { types, error: typesError } = useReportTypes(fetcher);
  const { library, error: libraryError, reload } = useLibrary(fetcher);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [picking, setPicking] = useState<{ folderId?: string | undefined } | null>(null);
  const [folderDialog, setFolderDialog] = useState<FolderDialog>(null);
  const [sharing, setSharing] = useState<ReportFolder | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** What the last standard reports install added (MOTHER-248); cleared by the next change. */
  const [notice, setNotice] = useState<string | null>(null);
  /** While the standard reports install; the ref stops a second click before the button disables. */
  const [installing, setInstalling] = useState(false);
  const installRunning = useRef(false);
  /** The folder the list reopens on after an install: its key remounts ReportList there. */
  const [openedFolder, setOpenedFolder] = useState<string | null>(null);
  const folders = library?.folders ?? [];

  /** Runs a change, then reloads the library; a refusal shows above the list. */
  const act = (work: () => Promise<unknown>, prefix = ''): Promise<void> => {
    setNotice(null);
    return work().then(
      () => { setProblem(null); return reload(); },
      (err: unknown) => setProblem(`${prefix}${errorText(err)}`),
    );
  };

  /**
   * Installs the standard reports once, reloads the library, then shows the folder and says how
   * many reports the server added.
   */
  const installStandard = () => {
    if (installRunning.current) return;
    installRunning.current = true;
    setInstalling(true);
    let installed: { folderId: string; created: string[] } | null = null;
    void act(async () => { installed = await installStandardReports(fetcher); }).finally(() => {
      installRunning.current = false;
      setInstalling(false);
      if (installed) {
        setOpenedFolder(installed.folderId);
        setNotice(standardReportsNotice(installed.created.length));
      }
    });
  };
  const offerStandard = library?.me.admin === true;
  const standardLabel = hasStandardFolder(folders) ? 'Update standard reports' : 'Add standard reports';

  const listed: SavedReportSummary[] = (library?.reports ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    description: r.description,
    folderId: r.folderId,
    format: r.format,
    reportTypeLabel: typeIn(types, r.reportType)?.label,
    owner: r.owner,
    updatedBy: r.updatedBy,
    updatedAt: r.updatedAt,
    // The Recent view lists what the viewer opened last, falling back to when it was saved.
    lastRunAt: r.lastRunAt,
    favorite: r.favorite,
    canEdit: r.canEdit,
  }));

  const parent = folderDialog?.mode === 'new' && folderDialog.parentId
    ? folders.find((f) => f.id === folderDialog.parentId)
    : undefined;

  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader
          className="rounded-[var(--radius-lg)] border shadow-[var(--shadow-xs)]"
          icon={<ReportIcon />}
          eyebrow={<span className="sr-only">Reports</span>}
          title="Reports"
          actions={(
            <>
              <Button asChild variant="neutral"><Link to={MY_DASHBOARD_ROUTE}>Open My Dashboard</Link></Button>
              {offerStandard ? <Button onClick={installStandard} disabled={installing}>{standardLabel}</Button> : null}
            </>
          )}
        />
        {notice ? <InlineMessage tone="success" role="status">{notice}</InlineMessage> : null}
        {problem ? <InlineMessage tone="error" role="alert">{problem}</InlineMessage> : null}
        {typesError ? <InlineMessage tone="error" role="alert">{typesError}</InlineMessage> : null}
        {libraryError ? <InlineMessage tone="error" role="alert">{libraryError}</InlineMessage> : null}
        {library ? (
          <ReportList
            key={openedFolder ?? undefined}
            variant="embedded"
            reports={listed}
            folders={folders}
            defaultFolderId={openedFolder ?? params.get('folder') ?? undefined}
            currentUser={library.me.name}
            timeZone={TIME_ZONE}
            onOpen={(r) => navigate(`${REPORTS_ROUTE}/${r.id}`)}
            onEdit={(r) => navigate(`${REPORTS_ROUTE}/${r.id}/edit`)}
            onNew={(folderId) => setPicking({ folderId })}
            onDelete={(r) => act(() => deleteReport(fetcher, r.id))}
            onDuplicate={(r) => act(() => duplicateReport(fetcher, r.id))}
            onRename={(r, name) => act(() => renameReport(fetcher, r.id, name))}
            onMove={(r, folder) => act(() => moveReport(fetcher, r.id, folder.id))}
            onFavoriteChange={(r, on) => act(() => setFavorite(fetcher, r.id, on))}
            onNewFolder={(parentId) => setFolderDialog({ mode: 'new', parentId })}
            onRenameFolder={(folder) => setFolderDialog({ mode: 'rename', folder })}
            onShareFolder={setSharing}
            onDeleteFolder={(folder) => act(() => deleteFolder(fetcher, folder.id), `${folder.name}: `)}
            onOpenFolderAsDashboard={(folder) => navigate(reportDashboardHref(folder.id))}
          />
        ) : libraryError ? null : (
          <Spinner />
        )}
        <ReportTypePicker
          open={picking !== null}
          onOpenChange={(open) => { if (!open) setPicking(null); }}
          types={types ?? []}
          favorites={library?.favoriteTypes ?? []}
          usage={library?.typeCounts ?? {}}
          onFavoriteChange={(key, on) => act(() => setFavoriteType(fetcher, key, on))}
          onSelect={(type) => {
            const folder = picking?.folderId ? `&folder=${encodeURIComponent(picking.folderId)}` : '';
            navigate(`${REPORTS_ROUTE}/new?type=${encodeURIComponent(type.key)}${folder}`);
          }}
        />
        <ReportFolderDialog
          open={folderDialog !== null}
          onOpenChange={(open) => { if (!open) setFolderDialog(null); }}
          folder={folderDialog?.mode === 'rename' ? folderDialog.folder : undefined}
          parent={parent}
          canPublish={library?.me.admin === true}
          everyone="everyone at Olytic"
          onSave={({ name, everyone }) => {
            if (folderDialog?.mode === 'rename') act(() => updateFolder(fetcher, folderDialog.folder.id, { name }));
            else if (folderDialog?.mode === 'new') act(() => createFolder(fetcher, name, folderDialog.parentId, everyone));
          }}
        />
        {sharing && library ? (
          <ViewShareDialog
            open
            onOpenChange={(open) => { if (!open) setSharing(null); }}
            noun="folder"
            sharedNote="Anyone it’s shared with can add reports and folders to it, and edit its reports."
            everyone="Everyone at Olytic"
            everyoneNote="Everyone can see it. Only admins can add to it or change it."
            view={{
              id: sharing.id,
              label: sharing.name,
              visibility: sharing.visibility,
              owner: sharing.owner ?? null,
              sharedWith: sharing.sharedWith ?? [],
              canEdit: sharing.canEdit === true,
              canManage: sharing.canManage === true,
            }}
            people={library.people}
            currentUserId={library.me.id}
            canPublish={library.me.admin}
            readOnlyReason={sharing.visibility === 'everyone'
              ? 'Only admins can change a public folder.'
              : 'Only its owner can change who sees it.'}
            onSave={(change) => act(() => updateFolder(fetcher, sharing.id, {
              name: change.label, visibility: change.visibility, sharedWith: change.sharedWith,
            }))}
          />
        ) : null}
      </Stack>
    </PageContent>
  );
}
